import { DEG, TAU, clamp01 } from '../../core/math';
import { bakeCurve, sampleBaked, type Curve } from '../../core/params/curve';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import type { Ctx2D, RenderTarget, TechniqueContext } from '../types';
import type { HalftoneValues } from './params';

/**
 * Halftone screen driven by the text field.
 *
 * The difference from the post-processing halftone is that this one reads the *field*,
 * not the composed pixels: it can size dots by distance to the letterforms rather than
 * by brightness, which produces the soft printed aura a pixel-based screen cannot.
 *
 * CMYK mode lays four screens at the traditional angles (15°, 75°, 0°, 45°). Those
 * angles are chosen to keep the rosette pattern from collapsing into a moiré; using the
 * same angle four times would.
 */
export interface HalftoneDot {
  x: number;
  y: number;
  size: number;
  value: number;
  channel: number;
}

export interface HalftoneState {
  dots: HalftoneDot[];
  palette: ResolvedPalette;
  sizeCurve: Float32Array;
  channelsDone: number;
  built: boolean;
}

const CMYK_ANGLES = [15, 75, 0, 45];
const CMYK_COLORS = ['#00aeef', '#ec008c', '#fff200', '#1c1c1c'];

function values(ctx: TechniqueContext): HalftoneValues {
  return ctx.values as unknown as HalftoneValues;
}

export function init(ctx: TechniqueContext): HalftoneState {
  const v = values(ctx);
  return {
    dots: [],
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    sizeCurve: bakeCurve(v.sizeCurve as Curve),
    channelsDone: 0,
    built: false,
  };
}

export function step(state: HalftoneState, _dt: number, ctx: TechniqueContext): HalftoneState {
  if (state.built) return state;
  const v = values(ctx);

  const channel = state.channelsDone;
  const totalChannels = v.cmyk ? 4 : 1;
  const angle = (v.cmyk ? CMYK_ANGLES[channel] : v.angle) * DEG;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const pitch = Math.max(2, v.pitch);
  const { width, height } = ctx;
  const cx = width / 2;
  const cy = height / 2;

  // Exact lattice bounds from the rotated canvas corners.
  let minI = Infinity;
  let maxI = -Infinity;
  let minJ = Infinity;
  let maxJ = -Infinity;
  for (const [px, py] of [
    [0, 0],
    [width, 0],
    [0, height],
    [width, height],
  ]) {
    const dx = px - cx;
    const dy = py - cy;
    const li = (dx * cos + dy * sin) / pitch;
    const lj = (-dx * sin + dy * cos) / pitch;
    minI = Math.min(minI, li);
    maxI = Math.max(maxI, li);
    minJ = Math.min(minJ, lj);
    maxJ = Math.max(maxJ, lj);
  }

  for (let j = Math.floor(minJ) - 1; j <= Math.ceil(maxJ) + 1; j++) {
    for (let i = Math.floor(minI) - 1; i <= Math.ceil(maxI) + 1; i++) {
      const lx = i * pitch;
      const ly = j * pitch;
      const x = cx + lx * cos - ly * sin;
      const y = cy + lx * sin + ly * cos;
      if (x < -pitch || y < -pitch || x > width + pitch || y > height + pitch) continue;

      let value: number;
      if (v.useDistance) {
        value = 1 - clamp01(Math.abs(ctx.field.distance(x, y)) / Math.max(1, v.distanceReach));
      } else {
        value = ctx.field.coverage(x, y);
      }
      if (value < 0.004) continue;

      const size = clamp01(sampleBaked(state.sizeCurve, value)) * pitch * v.maxSize;
      if (size < 0.15) continue;
      state.dots.push({ x, y, size, value, channel });
    }
  }

  state.channelsDone++;
  if (state.channelsDone >= totalChannels) state.built = true;
  return state;
}

export function isComplete(state: HalftoneState): boolean {
  return state.built;
}

function traceDot(path: Path2D, shape: string, x: number, y: number, size: number, pitch: number, angle: number): void {
  switch (shape) {
    case 'square':
      path.rect(x - size / 2, y - size / 2, size, size);
      break;
    case 'line': {
      const c = Math.cos(angle) * pitch * 0.5;
      const s = Math.sin(angle) * pitch * 0.5;
      path.moveTo(x - c, y - s);
      path.lineTo(x + c, y + s);
      break;
    }
    case 'cross':
      path.moveTo(x - size / 2, y);
      path.lineTo(x + size / 2, y);
      path.moveTo(x, y - size / 2);
      path.lineTo(x, y + size / 2);
      break;
    case 'wave': {
      // A short sine arc whose amplitude follows the dot value.
      const half = pitch * 0.5;
      path.moveTo(x - half, y);
      for (let t = -half; t <= half; t += pitch / 8) {
        path.lineTo(x + t, y + Math.sin((t / pitch) * TAU) * size * 0.5);
      }
      break;
    }
    case 'concentric': {
      const rings = Math.max(1, Math.round(size / 2.5));
      for (let r = 1; r <= rings; r++) {
        const rr = (size / 2) * (r / rings);
        path.moveTo(x + rr, y);
        path.arc(x, y, rr, 0, TAU);
      }
      break;
    }
    default:
      path.moveTo(x + size / 2, y);
      path.arc(x, y, size / 2, 0, TAU);
  }
}

const STROKED = new Set(['line', 'cross', 'wave', 'concentric']);

export function draw(state: HalftoneState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c: Ctx2D = target.ctx;
  const pitch = Math.max(2, v.pitch);
  const stroked = STROKED.has(v.shape);

  const channels = v.cmyk ? 4 : 1;
  for (let channel = 0; channel < channels; channel++) {
    const angle = (v.cmyk ? CMYK_ANGLES[channel] : v.angle) * DEG;

    if (v.cmyk) {
      // Multiply is how process inks actually combine on paper.
      c.save();
      c.globalCompositeOperation = 'multiply';
      c.globalAlpha = v.opacity * v.cmykSpread;
      c.fillStyle = CMYK_COLORS[channel];
      c.strokeStyle = CMYK_COLORS[channel];
    }

    // Bucket by colour when using a gradient, so the whole screen is a few paths.
    const buckets = new Map<string, Path2D>();
    for (const dot of state.dots) {
      if (dot.channel !== channel) continue;
      const color = v.cmyk
        ? CMYK_COLORS[channel]
        : v.colorMode === 'gradient'
          ? state.palette.at(dot.value, v.opacity)
          : state.palette.stop(0, v.opacity);
      let path = buckets.get(color);
      if (!path) {
        path = new Path2D();
        buckets.set(color, path);
      }
      traceDot(path, v.shape, dot.x, dot.y, dot.size, pitch, angle);
    }

    c.lineWidth = Math.max(0.4, pitch * 0.18);
    c.lineCap = 'round';
    for (const [color, path] of buckets) {
      if (stroked) {
        c.strokeStyle = v.cmyk ? CMYK_COLORS[channel] : color;
        c.stroke(path);
      } else {
        c.fillStyle = v.cmyk ? CMYK_COLORS[channel] : color;
        c.fill(path);
      }
    }

    if (v.cmyk) c.restore();
  }
}

export function elementCount(state: HalftoneState): number {
  return state.dots.length;
}
